import { Link } from 'react-router-dom';
import { MessageSquare, ExternalLink, Clock, Newspaper } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

// All party colors for tags
const partyColors = {
  labour: 'text-red-600 border-red-600',
  conservative: 'text-blue-600 border-blue-600',
  libdem: 'text-orange-500 border-orange-500',
  snp: 'text-yellow-600 border-yellow-600',
  reform: 'text-cyan-600 border-cyan-600',
  green: 'text-green-600 border-green-600',
  plaid: 'text-emerald-600 border-emerald-600',
  dup: 'text-red-800 border-red-800',
  sinnfein: 'text-green-800 border-green-800'
};

const partyLabels = {
  labour: 'Labour',
  conservative: 'Conservative',
  libdem: 'Lib Dem',
  snp: 'SNP',
  reform: 'Reform UK',
  green: 'Green',
  plaid: 'Plaid Cymru',
  dup: 'DUP',
  sinnfein: 'Sinn Féin'
};

export const NewsCard = ({ article, featured = false }) => {
  // Use published_date if available, otherwise fall back to created_at
  const displayDate = article.published_date || article.created_at;
  const formattedDate = formatDistanceToNow(new Date(displayDate), { addSuffix: true });
  const fullDate = format(new Date(displayDate), 'dd MMM yyyy');
  
  // Get a meaningful content preview
  const contentPreview = article.summary || article.content || '';
  const truncatedContent = contentPreview.length > 200 
    ? contentPreview.slice(0, 200).trim() + '...' 
    : contentPreview;

  return (
    <article 
      className={`news-card group ${featured ? 'news-card-featured' : ''}`}
      data-testid={`news-card-${article.id}`}
    >
      <div>
        {/* Header: Party Tag + Source */}
        <div className="flex items-center justify-between mb-4 gap-2">
          <span 
            className={`text-xs font-mono font-medium tracking-wider uppercase border px-2 py-1 ${partyColors[article.party] || 'text-slate-600 border-slate-600'}`}
            data-testid={`party-tag-${article.party}`}
          >
            {partyLabels[article.party]}
          </span>
          {article.source_name && (
            <span className="text-xs text-slate-500 font-mono truncate max-w-[120px]" title={article.source_name}>
              {article.source_name}
            </span>
          )}
        </div>

        {/* Title - clickable to article page */}
        <Link to={`/article/${article.id}`}>
          <h3 
            className={`font-serif font-medium leading-tight mb-3 group-hover:underline decoration-1 underline-offset-4 ${
              featured ? 'text-2xl md:text-3xl' : 'text-lg'
            }`}
            data-testid="news-card-title"
          >
            {article.title}
          </h3>
        </Link>

        {/* Content Preview - more content shown */}
        <p className={`text-slate-600 text-sm leading-relaxed ${featured ? 'line-clamp-5' : 'line-clamp-3'}`}>
          {truncatedContent}
        </p>

        {/* Region tag if available */}
        {article.region && (
          <span className="inline-block text-xs text-slate-500 bg-slate-100 px-2 py-1 mt-3 font-mono">
            {article.region}
          </span>
        )}

        {/* Politician Name */}
        {article.politician_name && (
          <p className="text-sm font-medium mt-3">
            Re: {article.politician_name}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
        <div className="flex items-center space-x-4 text-xs text-slate-500">
          <span className="flex items-center space-x-1 font-mono" title={fullDate}>
            <Clock className="h-3 w-3" />
            <span>{formattedDate}</span>
          </span>
          <span className="flex items-center space-x-1">
            <MessageSquare className="h-3 w-3" />
            <span>{article.comment_count}</span>
          </span>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Read more link - to article page */}
          <Link 
            to={`/article/${article.id}`}
            className="text-xs font-medium text-slate-600 hover:text-slate-900 flex items-center space-x-1"
            data-testid="read-more-link"
          >
            <Newspaper className="h-3 w-3" />
            <span>Read more</span>
          </Link>
          
          {/* Source link */}
          {article.source_url && (
            <a 
              href={article.source_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-slate-400 hover:text-slate-600 flex items-center space-x-1"
              data-testid="source-link"
            >
              <span>Source</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

export default NewsCard;
